import {
  type FamilyContextType,
  type ApiDataType,
  type FamilyMemberType,
} from 'types/services/Family'

import { addressMask, dateMask } from './masks'
import { questionLabels } from './questionLabels'
import {
  getAge,
  isChild,
} from './utils'

import { 
  getSummationLastMonthIncomePerMember,
  getSomeoneWorks,
  getNumberOfWorkers
 } from 'utils/familyUtils'

/*
  🔵 Formata os dados que serão utilizados, colocando:
      - question: A pergunta que o atributo responde
      - answer: A resposta, que na maioria das vezes é o valor do atributo
      - fildName: É utilizado para servir como uma key única para cada atributo
      - type: O tipo do dado, para facilitar na formatação do dado

  🔵 A verificação de base ocorre porque alguns dados se encontram em locais
    diferentes no dado que vem do back, de acordo com a base, então não podemos 
    acessar-los da mesma forma.

  🔴 IMPORTANTE: AS BASES DEVEM TER OS MESMOS ATRIBUTOS❗
*/

export function handleData(data: ApiDataType[]): FamilyContextType[] {
  return data.map(({ base, family, hasTimeout }: ApiDataType) => {
    const questionsFormatted: FamilyContextType = {
      base,
      hasTimeout,
      family:
        family !== undefined
          ? {
              ...(base === 'agents' && {
                pregnantInHome: {
                  question: questionLabels.pregnantInHome,
                  answer: family?.pregnantInHome,
                  fieldName: 'pregnantInHome',
                  type: 'text',
                },
              }),
              ...(base === 'cadunico' && {
                summationLastMonthIncomePerMember: {
                  question: questionLabels.summationLastMonthIncomePerMember,
                  answer: [family.familyHead, ...family.members].reduce(
                    getSummationLastMonthIncomePerMember,
                    0
                  ),
                  fieldName: 'getSummationLastMonthIncomePerMember',
                  type: 'money',
                },
                hasChildLabor: {
                  question: questionLabels.hasChildLabor,
                  answer: family?.hasChildLabor,
                  fieldName: 'hasChildLabor',
                  type: 'boolean',
                },
              }),
              familyCode: family?.familyCode,
              familyHead: {
                birthRegister: {
                  question: questionLabels.birthRegister,
                  answer: family.familyHead?.birthRegister,
                  fieldName: 'birthRegister',
                  type: 'boolean',
                },
                name: family?.familyHead?.name,
                role: family?.familyHead?.role,
                maritalStatus: family?.familyHead?.maritalStatus,
                kinship: family?.familyHead?.kinship,
                color: family?.familyHead?.color,
                gender: family?.familyHead?.gender,
                motherName: family?.familyHead?.motherName,
                fatherName: family?.familyHead?.fatherName,
                age: getAge(family?.familyHead?.birthDate),
                nis: family?.familyHead?.nis,
                registerType: family?.familyHead?.registerType,
                cpf: family?.familyHead?.cpf,
              },
              collectWay: family?.collectWay,
              collectDate: dateMask(family?.collectDate),
              email: family?.email,
              phone: family?.phone,
              otherPhone: family?.otherPhone,
              workAndIncome: {
                ...(base === 'agents' && {
                  someoneWorks: {
                    question: questionLabels.someoneWorks,
                    answer: family?.workAndIncome?.someoneWorks,
                    fieldName: 'workAndIncome.someoneWorks',
                    type: 'text',
                  },
                  numberOfWorkers: {
                    question: questionLabels.numberOfWorkers,
                    answer: family?.workAndIncome?.numberOfWorkers,
                    fieldName: 'workAndIncome.numberOfWorkers',
                    type: 'text',
                  },
                  sourceOfIncome: {
                    question: questionLabels.sourceOfIncome,
                    answer: family?.workAndIncome?.sourceOfIncome,
                    fieldName: 'workAndIncome.sourceOfIncome',
                    type: 'text',
                  },
                  lastMonthIncome: {
                    question: questionLabels.lastMonthIncome,
                    answer: family?.workAndIncome?.lastMonthIncome,
                    fieldName: 'workAndIncome.lastMonthIncome',
                    type: 'money',
                  },
                  madeQualificationCourse: {
                    question: questionLabels.madeQualificationCourse,
                    answer: family?.workAndIncome?.madeQualificationCourse,
                    fieldName: 'workAndIncome.madeQualificationCourse',
                    type: 'text',
                  },
                  wantToMakeQualificationCourse: {
                    question: questionLabels.wantToMakeQualificationCourse,
                    answer:
                      family?.workAndIncome?.wantToMakeQualificationCourse,
                    fieldName: 'workAndIncome.wantToMakeQualificationCourse',
                    type: 'text',
                  },
                }),
                ...(base === 'cadunico' && {
                  someoneWorks: {
                    question: questionLabels.someoneWorks,
                    answer: getSomeoneWorks(family.members),
                    fieldName: 'workAndIncome.someoneWorks',
                    type: 'text',
                  },
                  numberOfWorkers: {
                    question: questionLabels.numberOfWorkers,
                    answer: getNumberOfWorkers(family),
                    fieldName: 'workAndIncome.numberOfWorkers',
                    type: 'text',
                  },
                  familyMeanCapital: {
                    question: questionLabels.familyMeanCapital,
                    answer: family?.workAndIncome?.familyMeanCapital,
                    fieldName: 'workAndIncome.familyMeanCapital',
                    type: 'money',
                  },
                  waterExpense: {
                    question: questionLabels.waterExpense,
                    answer: family?.expensesInfo?.waterExpense,
                    fieldName: 'expensesInfo.waterExpense',
                    type: 'money',
                  },
                  foodExpense: {
                    question: questionLabels.foodExpense,
                    answer: family?.expensesInfo?.foodExpense,
                    fieldName: 'expensesInfo.foodExpense',
                    type: 'money',
                  },
                  rentExpense: {
                    question: questionLabels.rentExpense,
                    answer: family?.expensesInfo?.rentExpense,
                    fieldName: 'expensesInfo.rentExpense',
                    type: 'money',
                  },
                  lightingExpense: {
                    question: questionLabels.lightingExpense,
                    answer: family?.expensesInfo?.lightingExpense,
                    fieldName: 'expensesInfo.lightingExpense',
                    type: 'money',
                  },
                  remedyExpense: {
                    question: questionLabels.remedyExpense,
                    answer: family?.expensesInfo?.remedyExpense,
                    fieldName: 'expensesInfo.remedyExpense',
                    type: 'money',
                  },
                  transportExpense: {
                    question: questionLabels.transportExpense,
                    answer: family?.expensesInfo?.transportExpense,
                    fieldName: 'expensesInfo.transportExpense',
                    type: 'money',
                  },
                  gasExpense: {
                    question: questionLabels.gasExpense,
                    answer: family?.expensesInfo?.gasExpense,
                    fieldName: 'expensesInfo.gasExpense',
                    type: 'money',
                  },
                  totalExpense: {
                    question: questionLabels.totalExpense,
                    answer:
                      parseFloat(family?.expensesInfo?.waterExpense ?? 0) +
                      parseFloat(family?.expensesInfo?.foodExpense ?? 0) +
                      parseFloat(family?.expensesInfo?.rentExpense ?? 0) +
                      parseFloat(family?.expensesInfo?.lightingExpense ?? 0) +
                      parseFloat(family?.expensesInfo?.remedyExpense ?? 0) +
                      parseFloat(family?.expensesInfo?.transportExpense ?? 0) +
                      parseFloat(family?.expensesInfo?.gasExpense ?? 0),
                    fieldName: 'expensesInfo.totalExpense',
                    type: 'money',
                  },
                }),
              },
              residenceInfo: {
                ...(base === 'agents' && {
                  cep: {
                    question: questionLabels.cep,
                    answer: family?.residenceInfo?.cep,
                    fieldName: 'residenceInfo.cep',
                    type: 'text',
                  },
                  city: {
                    question: questionLabels.city,
                    answer: family?.residenceInfo?.city,
                    fieldName: 'residenceInfo.city',
                    type: 'text',
                  },
                  drinkableWater: {
                    question: questionLabels.drinkableWater,
                    answer: family?.residenceInfo?.drinkableWater,
                    fieldName: 'residenceInfo.drinkableWater',
                    type: 'text',
                  },
                  garbageCollectionFrequency: {
                    question: questionLabels.garbageCollectionFrequency,
                    answer: family?.residenceInfo?.garbageCollectionFrequency,
                    fieldName: 'residenceInfo.garbageCollectionFrequency',
                    type: 'text',
                  },
                  placesToPlayNearby: {
                    question: questionLabels.placesToPlayNearby,
                    answer: family?.residenceInfo?.placesToPlayNearby,
                    fieldName: 'residenceInfo.placesToPlayNearby',
                    type: 'text',
                  },
                  culturalActivitiesNearby: {
                    question: questionLabels.culturalActivitiesNearby,
                    answer: family?.residenceInfo?.culturalActivitiesNearby,
                    fieldName: 'residenceInfo.culturalActivitiesNearby',
                    type: 'text',
                  },
                  sportsActivitiesPlaceNearby: {
                    question: questionLabels.sportsActivitiesPlaceNearby,
                    answer: family?.residenceInfo?.sportsActivitiesPlaceNearby,
                    fieldName: 'residenceInfo.sportsActivitiesPlaceNearby',
                    type: 'text',
                  },
                  dangerZone: {
                    question: questionLabels.dangerZone,
                    answer: family?.residenceInfo?.dangerZone,
                    fieldName: 'residenceInfo.dangerZone',
                    type: 'text',
                  },
                }),
                ...(base === 'cadunico' && {
                  zone: {
                    question: questionLabels.zone,
                    answer: family?.residenceInfo?.zone,
                    fieldName: 'residenceInfo.zone',
                    type: 'text',
                  },
                  paving: {
                    question: questionLabels.paving,
                    answer: family?.residenceInfo?.paving,
                    fieldName: 'residenceInfo.paving',
                    type: 'text',
                  },
                  garbageCollectionFrequency: {
                    question: questionLabels.garbageDisposal,
                    answer: family?.residenceInfo?.garbageDisposal,
                    fieldName: 'residenceInfo.garbageDisposal',
                    type: 'text',
                  },
                  sanitaryDrainage: {
                    question: questionLabels.sanitaryDrainage,
                    answer: family?.residenceInfo?.sanitaryDrainage,
                    fieldName: 'residenceInfo.sanitaryDrainage',
                    type: 'text',
                  },
                  houseZone: {
                    question: questionLabels.houseZone, //
                    answer: family?.residenceInfo?.houseZone,
                    fieldName: 'residenceInfo.houseZone',
                    type: 'text',
                  },
                  floorMaterial: {
                    question: questionLabels.floorMaterial,
                    answer: family?.residenceInfo?.floorMaterial,
                    fieldName: 'residenceInfo.floorMaterial',
                    type: 'text',
                  },
                  numberOfRooms: {
                    question: questionLabels.numberOfRooms,
                    answer: family?.residenceInfo?.numberOfRooms,
                    fieldName: 'residenceInfo.numberOfRooms',
                    type: 'text',
                  },
                  numberOfBedrooms: {
                    question: questionLabels.numberOfBedrooms,
                    answer: family?.residenceInfo?.numberOfBedrooms,
                    fieldName: 'residenceInfo.numberOfBedrooms',
                    type: 'text',
                  },
                  numberOfResidents: {
                    question: questionLabels.numberOfResidents,
                    answer: family?.residenceInfo?.numberOfResidents,
                    fieldName: 'residenceInfo.numberOfResidents',
                    type: 'text',
                  },
                  numberOfFamilies: {
                    question: questionLabels.numberOfFamilies,
                    answer: family?.residenceInfo?.numberOfFamilies,
                    fieldName: 'residenceInfo.numberOfFamilies',
                    type: 'text',
                  },
                }),
                address: {
                  question: questionLabels.address,
                  answer: addressMask(family?.residenceInfo?.address),
                  fieldName: 'residenceInfo.address',
                  type: 'text',
                },
                waterSuply: {
                  question: questionLabels.waterSuply,
                  answer: family?.residenceInfo?.watterSuply,
                  fieldName: 'residenceInfo.watterSuply',
                  type: 'text',
                },
                pipedWater: {
                  question: questionLabels.pipedWater,
                  answer: family?.residenceInfo?.pipedWater,
                  fieldName: 'residenceInfo.pipedWater',
                  type: 'boolean',
                },
                hasBathroom: {
                  question: questionLabels.hasBathroom,
                  answer: family?.residenceInfo?.hasBathroom,
                  fieldName: 'residenceInfo.hasBathroom',
                  type: 'boolean',
                },
                houseType: {
                  question: questionLabels.houseType,
                  answer: family?.residenceInfo?.houseType,
                  fieldName: 'residenceInfo.houseType',
                  type: 'text',
                },
                formOfLighting: {
                  question: questionLabels.formOfLighting,
                  answer: family?.residenceInfo?.formOfLighting,
                  fieldName: 'residenceInfo.formOfLighting',
                  type: 'text',
                },
                wallsMaterial: {
                  question: questionLabels.wallsMaterial,
                  answer: family?.residenceInfo?.wallsMaterial,
                  fieldName: 'residenceInfo.wallsMaterial',
                  type: 'text',
                },
              },
              culturalInfo: {
                ...(base === 'cadunico' && {
                  indigenousFamily: {
                    question: questionLabels.indigenousFamily,
                    answer: family?.culturalInfo?.indigenousFamily,
                    fieldName: 'culturalInfo.indigenousFamily',
                    type: 'boolean',
                  },
                  indigenousLand: {
                    question: questionLabels.indigenousLand,
                    answer: family?.culturalInfo?.indigenousLand,
                    fieldName: 'culturalInfo.indigenousLand',
                    type: 'text',
                  },
                  indigenousCommunity: {
                    question: questionLabels.indigenousCommunity,
                    answer: family?.culturalInfo?.indigenousCommunity,
                    fieldName: 'culturalInfo.indigenousCommunity',
                    type: 'text',
                  },
                  indigenousReserve: {
                    question: questionLabels.indigenousReserve,
                    answer: family?.culturalInfo?.indigenousReserve,
                    fieldName: 'culturalInfo.indigenousReserve',
                    type: 'text',
                  },
                  kilombolaFamily: {
                    question: questionLabels.kilombolaFamily,
                    answer: family?.culturalInfo?.kilombolaFamily,
                    fieldName: 'culturalInfo.kilombolaFamily',
                    type: 'boolean',
                  },
                  kilombolaCommunity: {
                    question: questionLabels.kilombolaCommunity,
                    answer: family?.culturalInfo?.kilombolaCommunity,
                    fieldName: 'culturalInfo.kilombolaCommunity',
                    type: 'text',
                  },
                }),
              },
              nutritionInfo: {
                ...(base === 'agents' && {
                  animalsForConsumption: {
                    question: questionLabels.animalsForConsumption,
                    answer: family?.nutritionInfo?.animalsForConsumption,
                    fieldName: 'nutritionInfo.animalsForConsumption',
                    type: 'text',
                  },
                  foodAvailabilityAfterCovid: {
                    question: questionLabels.foodAvailabilityAfterCovid,
                    answer: family?.nutritionInfo?.foodAvailabilityAfterCovid,
                    fieldName: 'nutritionInfo.foodAvailabilityAfterCovid',
                    type: 'text',
                  },
                  foodConcern: {
                    question: questionLabels.foodConcern,
                    answer: family?.nutritionInfo?.foodConcern,
                    fieldName: 'nutritionInfo.foodConcern',
                    type: 'text',
                  },
                  foodDeficiency: {
                    question: questionLabels.foodDeficiency,
                    answer: family?.nutritionInfo?.foodDeficiency,
                    fieldName: 'nutritionInfo.foodDeficiency',
                    type: 'text',
                  },
                  foodEndedEarly: {
                    question: questionLabels.foodEndedEarly,
                    answer: family?.nutritionInfo?.foodEndedEarly,
                    fieldName: 'nutritionInfo.foodEndedEarly',
                    type: 'text',
                  },
                  healthyFoodDeficiency: {
                    question: questionLabels.healthyFoodDeficiency,
                    answer: family?.nutritionInfo?.healthyFoodDeficiency,
                    fieldName: 'nutritionInfo.healthyFoodDeficiency',
                    type: 'text',
                  },
                  plantsForConsumption: {
                    question: questionLabels.plantsForConsumption,
                    answer: family?.nutritionInfo?.plantsForConsumption,
                    fieldName: 'nutritionInfo.plantsForConsumption',
                    type: 'text',
                  },
                  skipedMeal: {
                    question: questionLabels.skipedMeal,
                    answer: family?.nutritionInfo?.skipedMeal,
                    fieldName: 'nutritionInfo.skipedMeal',
                    type: 'text',
                  },
                }),
              },
              assistenceInfo: {
                ...(base === 'agents' && {
                  familyAssistenceEstablishment: {
                    question: questionLabels.familyAssistenceEstablishment,
                    answer:
                      family?.assistenceInfo?.familyAssistenceEstablishment,
                    fieldName: 'assistenceInfo.familyAssistenceEstablishment',
                    type: 'text',
                  },
                  healthAssistenceEstablishment: {
                    question: questionLabels.healthAssistenceEstablishment,
                    answer:
                      family?.assistenceInfo?.healthAssistenceEstablishment,
                    fieldName: 'assistenceInfo.healthAssistenceEstablishment',
                    type: 'text',
                  },
                  crasActivity: {
                    question: questionLabels.crasActivity,
                    answer: family?.assistenceInfo?.crasActivity,
                    fieldName: 'assistenceInfo.crasActivity',
                    type: 'text',
                  },
                  creasActivity: {
                    question: questionLabels.creasActivity,
                    answer: family?.assistenceInfo?.creasActivity,
                    fieldName: 'assistenceInfo.creasActivity',
                    type: 'text',
                  },
                  govenmentAid: {
                    question: questionLabels.govenmentAid,
                    answer: family?.assistenceInfo?.govenmentAid,
                    fieldName: 'assistenceInfo.govenmentAid',
                    type: 'text',
                  },
                }),
              },
              // members é um array com o familyHead no inicio e os
              // outros membros ordenados pelo nome de forma alfabética
              members: [
                family.familyHead,
                ...family.members.sort(
                  (a: FamilyMemberType, b: FamilyMemberType) => {
                    if (a.name === b.name) {
                      return 0
                    } else if (a.name < b.name) {
                      return -1
                    }
                    return 1
                  }
                ),
              ].map((member) => ({
                name: member?.name,
                role: member?.role,
                maritalStatus: member?.maritalStatus,
                kinship: member?.kinship,
                color: member?.color,
                gender: member?.gender,
                motherName: member?.motherName,
                fatherName: member?.fatherName,
                age: getAge(member?.birthDate),
                nis: member.nis,
                registerType: member.registerType,
                cpf: member.cpf,
                ...(base === 'cadunico' &&
                  isChild(getAge(member?.birthDate)) && {
                    childLabor: {
                      question: questionLabels.childLabor,
                      answer: member?.childLabor,
                      fieldName: 'member.childLabor',
                      type: 'boolean',
                    },
                  }),
                birthRegister: {
                  question: questionLabels.birthRegister,
                  answer: member?.birthRegister,
                  fieldName: 'member.birthRegister',
                  type: 'boolean',
                },
                health: {
                  ...(base === 'agents' &&
                    isChild(getAge(member?.birthDate)) && {
                      vaccinesCard: {
                        question: questionLabels.vaccinesCard,
                        answer: member?.health?.vaccinesCard,
                        fieldName: 'member.health.vaccinesCard',
                        type: 'text',
                      },
                      vaccinesUpToDate: {
                        question: questionLabels.vaccinesUpToDate,
                        answer: member?.health?.vaccinesUpToDate,
                        fieldName: 'member.health.vaccinesUpToDate',
                        type: 'text',
                      },
                      familyVisitingProgram: {
                        question: questionLabels.familyVisitingProgram,
                        answer: member?.health?.familyVisitingProgram,
                        fieldName: 'member.health.familyVisitingProgram',
                        type: 'text',
                      },
                      familyHealthTeam: {
                        question: questionLabels.familyHealthTeam,
                        answer: member?.health?.familyHealthTeam,
                        fieldName: 'member.health.familyHealthTeam',
                        type: 'text',
                      },
                      breastfedFirstHour: {
                        question: questionLabels.breastfedFirstHour,
                        answer: member?.health?.breastfedFirstHour,
                        fieldName: 'member.health.breastfedFirstHour',
                        type: 'text',
                      },
                      breastfedUntilAge: {
                        question: questionLabels.breastfedUntilAge,
                        answer: member?.health?.breastfedUntilAge,
                        fieldName: 'member.health.breastfedUntilAge',
                        type: 'text',
                      },
                    }),
                  ...(base === 'cadunico' &&
                    isChild(getAge(member?.birthDate)) && {
                      thirdPartyAssistance: {
                        question: questionLabels.thirdPartyAssistance,
                        answer: member?.health?.thirdPartyAssistance,
                        fieldName: 'member.health.thirdPartyAssistance',
                        type: 'text',
                      },
                    }),
                  ...(isChild(getAge(member?.birthDate)) && {
                    disability: {
                      question: questionLabels.disability,
                      answer: member?.health?.disability,
                      fieldName: 'member.health.disability',
                      type: 'text',
                    },
                  }),
                },
                education: {
                  ...(base === 'cadunico' && {
                    attendsSchool: {
                      question: questionLabels.attendsSchool,
                      answer: member?.education?.attendsSchool,
                      fieldName: 'member.education.attendsSchool',
                      type: 'text',
                    },
                    currentSchoolGrade: {
                      question: questionLabels.currentSchoolGrade,
                      answer: member?.education?.currentSchoolGrade,
                      fieldName: 'member.education.currentSchoolGrade',
                      type: 'text',
                    },
                    currentSchoolName: {
                      question: questionLabels.currentSchoolName, //
                      answer: member?.education?.currentSchoolName,
                      fieldName: 'member.education.currentSchoolName',
                      type: 'text',
                    },
                    highestEducationLevel: {
                      question: questionLabels.highestEducationLevel,
                      answer: member?.education?.highestEducationLevel,
                      fieldName: 'member.education.highestEducationLevel',
                      type: 'text',
                    },
                    canReadWrite: {
                      question: questionLabels.canReadWrite,
                      answer: member?.education?.canReadWrite,
                      fieldName: 'member.education.canReadWrite',
                      type: 'boolean',
                    },
                  }),
                  currentCourse: {
                    question: questionLabels.currentCourse, //
                    answer: member?.education?.currentCourse,
                    fieldName: 'member.education.currentCourse',
                    type: 'text',
                  },
                },
                workAndIncome: {
                  ...(base === 'cadunico' && {
                    awayFromWork: {
                      question: questionLabels.awayFromWork,
                      answer: member?.workAndIncome?.awayFromWork,
                      fieldName: 'member.workAndIncome.awayFromWork',
                      type: 'boolean',
                    },
                    workedLastWeek: {
                      question: questionLabels.workedLastWeek,
                      answer: member?.workAndIncome?.workedLastWeek,
                      fieldName: 'member.workAndIncome.workedLastWeek',
                      type: 'boolean',
                    },
                    lastMonthWorkIncome: {
                      question: questionLabels.lastMonthWorkIncome,
                      answer: member?.workAndIncome?.lastMonthIncome,
                      fieldName: 'member.workAndIncome.lastMonthIncome',
                      type: 'money',
                    },
                    agricultureWork: {
                      question: questionLabels.agricultureWork,
                      answer: member?.workAndIncome?.agricultureWork,
                      fieldName: 'member.workAndIncome.agricultureWork',
                      type: 'boolean',
                    },
                    jobType: {
                      question: questionLabels.jobType,
                      answer: member?.workAndIncome?.jobType,
                      fieldName: 'member.workAndIncome.jobType',
                      type: 'text',
                    },
                    workedLastYear: {
                      question: questionLabels.workedLastYear,
                      answer: member?.workAndIncome?.workedLastYear,
                      fieldName: 'member.workAndIncome.workedLastYear',
                      type: 'boolean',
                    },
                    lastYearWorkedMonths: {
                      question: questionLabels.lastYearWorkedMonths,
                      answer: member?.workAndIncome?.lastYearWorkedMonths,
                      fieldName: 'member.workAndIncome.lastYearWorkedMonths',
                      type: 'text',
                    },
                    lastYearWorkIncome: {
                      question: questionLabels.lastYearWorkIncome,
                      answer: member?.workAndIncome?.lastYearWorkIncome,
                      fieldName: 'member.workAndIncome.lastYearWorkIncome',
                      type: 'money',
                    },
                    otherIncomesPerMonth: {
                      question: questionLabels.otherIncomesPerMonth,
                      answer: member?.workAndIncome?.otherIncomesPerMonth,
                      fieldName: 'member.workAndIncome.otherIncomesPerMonth',
                      type: 'money',
                    },
                    retirementIncomesPerMonth: {
                      question: questionLabels.retirementIncomesPerMonth,
                      answer: member?.workAndIncome?.retirementIncomesPerMonth,
                      fieldName:
                        'member.workAndIncome.retirementIncomesPerMonth',
                      type: 'money',
                    },
                    donationIncomesPerMonth: {
                      question: questionLabels.donationIncomesPerMonth,
                      answer: member?.workAndIncome?.donationIncomesPerMonth,
                      fieldName: 'member.workAndIncome.donationIncomesPerMonth',
                      type: 'money',
                    },
                    alimonyIncomesPerMonth: {
                      question: questionLabels.alimonyIncomesPerMonth,
                      answer: member?.workAndIncome?.alimonyIncomesPerMonth,
                      fieldName: 'member.workAndIncome.alimonyIncomesPerMonth',
                      type: 'money',
                    },
                    unemploymentInsuranceIncomesPerMonth: {
                      question:
                        questionLabels.unemploymentInsuranceIncomesPerMonth,
                      answer:
                        member?.workAndIncome
                          ?.unemploymentInsuranceIncomesPerMonth,
                      fieldName:
                        'member.workAndIncome.unemploymentInsuranceIncomesPerMonth',
                      type: 'money',
                    },
                  }),
                },
              })),
            }
          : undefined,
    }

    return questionsFormatted
  })
}
